import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { toast } from "sonner";

const LOGO = "/ticketup_logo.png";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isLogin) await login(email, password);
      else await register({ email, password, name, role });
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      nav(next);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:flex bg-[#0A0A0A] text-white p-16 flex-col justify-between">
        <Link to="/" className="inline-block w-fit">
          <img src={LOGO} alt="TicketUp" className="h-10 w-auto [filter:brightness(0)_invert(1)]" />
        </Link>
        <div>
          <p className="label-eyebrow text-white/60">{isLogin ? "Welcome back" : "Join the show"}</p>
          <h2 className="font-display text-5xl xl:text-7xl mt-3 tracking-tighter leading-[0.95]">
            {isLogin ? "The show goes on." : "Your front-row seat starts here."}
          </h2>
          <p className="mt-6 text-neutral-400 max-w-md">
            {isLogin ? "Sign in to access your tickets and orders." : "Create an account to buy tickets and never miss a moment."}
          </p>
        </div>
        <div className="flex gap-12 text-sm text-neutral-500">
          <div><div className="font-display text-3xl text-white">2.4M+</div>tickets sold</div>
          <div><div className="font-display text-3xl text-white">540+</div>events / month</div>
          <div><div className="font-display text-3xl text-white">100%</div>verified</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="auth-form">
          <span className="label-eyebrow">{isLogin ? "Sign in" : "Create account"}</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">
            {isLogin ? "Welcome back." : "Let's go."}
          </h1>

          {err && <div className="mt-4 p-3 border border-red-500 text-red-600 text-sm rounded-xl" data-testid="auth-error">{err}</div>}

          <div className="mt-8 space-y-4">
            {!isLogin && (
              <div>
                <label className="text-xs uppercase tracking-widest font-bold">Full name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full mt-1 border border-neutral-300 px-4 py-3 focus:border-black outline-none rounded-xl"
                  data-testid="name-input" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest font-bold">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full mt-1 border border-neutral-300 px-4 py-3 focus:border-black outline-none rounded-xl"
                data-testid="email-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                minLength={6}
                className="w-full mt-1 border border-neutral-300 px-4 py-3 focus:border-black outline-none rounded-xl"
                data-testid="password-input" />
            </div>

            {!isLogin && (
              <div>
                <label className="text-xs uppercase tracking-widest font-bold">I want to</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setRole("user")}
                    className={`py-3 px-3 border text-sm font-bold rounded-xl ${role === "user" ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
                    data-testid="role-user">Buy tickets</button>
                  <button type="button" onClick={() => setRole("organizer")}
                    className={`py-3 px-3 border text-sm font-bold rounded-xl ${role === "organizer" ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
                    data-testid="role-organizer">Sell tickets</button>
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={busy} className="btn-red w-full mt-8 disabled:opacity-60" data-testid="auth-submit">
            {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </button>

          <p className="mt-6 text-sm text-neutral-600 text-center">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="font-bold underline hover:text-[#4F46E5]" data-testid="auth-toggle">
              {isLogin ? "Sign up" : "Sign in"}
            </Link>
          </p>

          <div className="mt-8 p-4 bg-[#F8F9FA] border border-neutral-200 text-xs text-neutral-600 rounded-xl">
            <p className="font-bold uppercase tracking-widest">Demo accounts</p>
            <p className="mt-1">User: user@ticketup.com / User@123</p>
            <p>Organizer: organizer@ticketup.com / Organizer@123</p>
            <p>Admin: admin@ticketup.com / Admin@123</p>
          </div>
        </form>
      </div>
    </div>
  );
}
